import axios from "axios";
import {useEffect, useState, useRef} from "react";
import {useNavigate} from "react-router-dom";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {checkOrderStatus, subscribeToOrderStatus, initializeSocket} from "../../../api/orderStatusSocket";
import {OrderStatus} from "../../../constants/enums/orderStatus";
import "./applepaymobile.css";
import {secondaryModals, secondOverlayState, selectedShippingAddress, loaderState} from "../../../Recoil/Atoms";
import {useSecondModal} from "../../../helpers/hooks/useSecondModal";
import {resetAllModals, prepareForSuccessNavigation} from "../../../helpers/utils/modalUtils";
// Removed cn import

const BASE_URL = process.env.REACT_APP_BASE_URL;
const TRANZILA_SCRIPT_URL = 'https://hf.tranzila.com/assets/js/thfapplepay.js';

const ApplePayMobileModal = ({data}) => {
  const modal = useSecondModal();
  const setModals = useSetRecoilState(secondaryModals);
  const [, setOverlay] = useRecoilState(secondOverlayState);
  const setPageLoader = useSetRecoilState(loaderState);
  const [isLoading, setIsLoading] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const [timer, setTimer] = useState(600);
  const [isSecondsPassed, setIsSecondsPassed] = useState(false);
  const [isRefreshClicked, setIsRefreshClicked] = useState(false);
  const selectedAddress = useRecoilValue(selectedShippingAddress);
  const [orderStatus, setOrderStatus] = useState(OrderStatus.NOT_PAID);
  const navigate = useNavigate();
  const [isApplePayScriptLoaded, setIsApplePayScriptLoaded] = useState(false);
  const [isPaymentInProgress, setIsPaymentInProgress] = useState(false);
  // PAID handled once: the Tranzila callback, the socket, the poll and the
  // timeout / error re-checks can all report the same payment.
  const doneRef = useRef(false);
  const scriptRef = useRef(null);
  const paymentTimeoutRef = useRef(null);
  const statusPollingRef = useRef(null);
  const applePayButtonRef = useRef(null); // Add ref for the button

  // GIFT-CARD orders (GC- prefixed id) route to /gift-success — every
  // payment-success navigation in this modal funnels through here.
  const goPaySuccess = (oid) => {
    if (oid && String(oid).startsWith("GC-")) {
      localStorage.removeItem("orderId");
      navigate(`/gift-success/${oid}`);
      return;
    }
    navigate("/payment-success/" + oid);
  };

  // Payment confirmed (socket, poll or a server re-check): close the modal at
  // once - no checkmark overlay, no delay; the success page's SuccessReveal is the
  // confirmation - clear the checkout and navigate.
  const succeed = (oid) => {
    if (doneRef.current) return;
    doneRef.current = true;
    window.successfulPaymentReceived = true;
    sessionStorage.setItem('successfulPaymentReceived', 'true');
    setOrderStatus(OrderStatus.PAID);
    handleClose(true);
    localStorage.clear();
    goPaySuccess(oid);
  };

  // Debugging function for consistent logging
  const debugLog = (message, data = {}) => {
    const timestamp = new Date().toISOString();
    const orderId = data?.orderId || localStorage.getItem("orderId") || "unknown";
    console.log(`[ApplePay Debug][Order: ${orderId}] ${message}`, {
      ...data,
      timestamp
    });
  };

  // Enhanced version of showErrorIfSafe that only shows errors for exceptional cases
  const showErrorIfSafe = async (errorType) => {
    // Create a unique trigger ID for tracing
    const triggerId = `trigger-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
    const orderId = localStorage.getItem("orderId");
    
    // Start a console group with visually distinctive styling
    console.group(`%c⚠️ Error Trigger: ${errorType} [ID: ${triggerId}]`, 
      'background: #fffacd; color: #b8860b; font-size: 14px; font-weight: bold; padding: 4px;');
    
    // Only show our custom error UI for exceptional cases that Apple Pay doesn't handle
    const isExceptionalCase = errorType === 'script_loading' || 
                              errorType === 'timeout' || 
                              errorType === 'order_status';
    
    if (!isExceptionalCase) {
      console.log(`%c🍎 DECISION: Not showing error UI for ${errorType} - letting Apple Pay handle it`, 
        'background: #e2f3f5; color: #0a3d62; font-size: 14px; font-weight: bold; padding: 4px;');
      debugLog(`Not showing error UI for ${errorType} - letting Apple Pay handle it`);
      console.groupEnd();
      return;
    }
    
    // Check if we've received a successful payment notification
    const successReceived = window.successfulPaymentReceived || 
                            sessionStorage.getItem('successfulPaymentReceived') === 'true';
    
    // Check if we're in the middle of navigating to success page
    const isNavigating = window.isNavigatingToSuccessPage || 
                         sessionStorage.getItem('isNavigatingToSuccessPage') === 'true';
    
    // Capture detailed state information
    const stateInfo = {
      triggerId,
      orderId,
      errorType,
      timestamp: new Date().toISOString(),
      successFlags: {
        windowFlag: !!window.successfulPaymentReceived,
        sessionStorageFlag: sessionStorage.getItem('successfulPaymentReceived') === 'true',
        navigatingFlag: isNavigating
      },
      componentState: {
        orderStatus,
        isPaymentInProgress,
        isApplePayScriptLoaded,
        isLoading,
        timer
      },
      // Capture storage state
      sessionStorage: Object.keys(sessionStorage).reduce((acc, key) => {
        acc[key] = sessionStorage.getItem(key);
        return acc;
      }, {}),
      localStorage: Object.keys(localStorage).reduce((acc, key) => {
        acc[key] = localStorage.getItem(key);
        return acc;
      }, {})
    };
    
    // Log the state information
    console.log('Current state:', stateInfo);
    
    // Log call stack to see where the error was triggered from
    console.log('Error trigger call stack:', new Error().stack);
    
    // Standard debug log
    debugLog(`Error modal trigger check [ID: ${triggerId}]`, {
      orderId,
      errorType,
      successReceived,
      isNavigating,
      paymentInProgress: isPaymentInProgress,
      currentOrderStatus: orderStatus
    });
    
    // Don't show error if we've received a successful payment or are navigating
    if (successReceived) {
      console.log(`%c✅ DECISION: Suppressing error ${errorType} - successful payment was received`, 
        'background: #98fb98; color: #006400; font-size: 14px; font-weight: bold; padding: 4px;');
      debugLog(`Suppressing error modal because successful payment was received`);
      console.groupEnd();
      return;
    }
    
    if (isNavigating) {
      console.log(`%c✅ DECISION: Suppressing error ${errorType} - navigation to success page in progress`, 
        'background: #98fb98; color: #006400; font-size: 14px; font-weight: bold; padding: 4px;');
      debugLog(`Suppressing error modal because navigation to success page is in progress`);
      console.groupEnd();
      return;
    }
    
    if (orderStatus === OrderStatus.PAID) {
      console.log(`%c✅ DECISION: Suppressing error ${errorType} - order status is PAID`, 
        'background: #98fb98; color: #006400; font-size: 14px; font-weight: bold; padding: 4px;');
      debugLog(`Suppressing error modal because order status is PAID`);
      console.groupEnd();
      return;
    }
    
    // Before showing error, check with server if payment might have succeeded
    if (orderId) {
      try {
        console.log('Checking server status before showing error...');
        debugLog(`Checking server status before showing error`);
        const resp = await checkOrderStatus();
        
        console.log('Server response:', resp?.data);
        
        if (resp && resp.data && resp.data.orderStatus === OrderStatus.PAID) {
          console.log(`%c✅ DECISION: Suppressing error ${errorType} - server reports payment is successful`, 
            'background: #98fb98; color: #006400; font-size: 14px; font-weight: bold; padding: 4px;');
          debugLog(`Server reports payment is successful, navigating to success instead of showing error`);
          succeed(orderId);
          console.groupEnd();
          return;
        }
      } catch (error) {
        console.error("Error checking server status:", error);
      }
    }
    
    // If we get here, it's safe to show the error for exceptional cases
    console.warn(`%c🚨 DECISION: Showing error ${errorType} - exceptional case that Apple Pay doesn't handle`, 
      'background: #ff6347; color: white; font-size: 14px; font-weight: bold; padding: 4px;');
    debugLog(`Showing error modal for exceptional case`, {
      errorType,
      triggerId,
      reason: "Exceptional case that Apple Pay doesn't handle"
    });
    
    // Store trigger info in sessionStorage for potential later analysis
    try {
      sessionStorage.setItem('lastApplePayErrorTrigger', JSON.stringify({
        errorType,
        triggerId,
        timestamp: new Date().toISOString(),
        orderId
      }));
    } catch (e) {
      console.error('Failed to store trigger info in sessionStorage:', e);
    }
    
    setModals(state => ({
      ...state,
      applePayError: {
        visible: true, 
        data: {errorType}
      }
    }));
    
    console.groupEnd();
  };

  // Function to load the Tranzila script
  const loadTranzilaScript = () => {
    debugLog("Loading payment scripts - preparing your payment experience...");
    debugLog("Initial data:", data);
    
    // Store payment start time for debugging
    sessionStorage.setItem("applePayStartTime", Date.now().toString());
    
    // Check if script is already loaded
    if (window.hfChargeApple) {
      debugLog("Payment scripts already loaded and ready to use!");
      setIsApplePayScriptLoaded(true);
      setIsLoading(false);
      setPageLoader(state => ({...state, showLoader: false}));
      return;
    }
    
    try {
      // Remove any existing scripts to avoid duplicates
      if (scriptRef.current) {
        try {
          document.body.removeChild(scriptRef.current);
        } catch (err) {
          console.error("ApplePayMobile: Error removing existing script:", err);
        }
      }
      
      // Create script element for jQuery
      const jqueryScript = document.createElement('script');
      jqueryScript.src = "https://code.jquery.com/jquery-3.6.0.min.js";
      jqueryScript.async = true;
      
      jqueryScript.onload = () => {
        debugLog("jQuery loaded successfully - loading Tranzila script next...");
        
        // Create script element for Tranzila
        const script = document.createElement('script');
        script.src = `${TRANZILA_SCRIPT_URL}?v=${Date.now()}`;
        script.async = true;
        
          script.onload = () => {
          debugLog("Tranzila payment script loaded successfully - ready for Apple Pay!");
          
          // Add noConflict after script loads
          const noConflictScript = document.createElement('script');
          noConflictScript.textContent = 'var $n = jQuery.noConflict(true);';
          document.body.appendChild(noConflictScript);
          
          setIsApplePayScriptLoaded(true);
          setIsLoading(false);
          setPageLoader(state => ({...state, showLoader: false}));
          
          // Verify that the function exists
          if (typeof window.hfChargeApple !== 'function') {
            debugLog("Script loaded but hfChargeApple function not found", {
              windowFunctions: Object.keys(window).filter(k => typeof window[k] === 'function').join(', ')
            });
            setIsLoading(false);
            setPageLoader(state => ({...state, showLoader: false}));
            showErrorIfSafe('script_loading');
          }
        };
        
        script.onerror = (error) => {
          debugLog("Tranzila script loading error:", error);
          setIsLoading(false);
          setPageLoader(state => ({...state, showLoader: false}));
          showErrorIfSafe('script_loading');
        };
        
        document.body.appendChild(script);
        scriptRef.current = script;
      };
      
      jqueryScript.onerror = (error) => {
        debugLog("jQuery loading error:", error);
        setIsLoading(false);
        setPageLoader(state => ({...state, showLoader: false}));
        showErrorIfSafe('script_loading');
      };
      
      document.body.appendChild(jqueryScript);
    } catch (err) {
      debugLog("Error in script setup:", err);
      setIsLoading(false);
      setPageLoader(state => ({...state, showLoader: false}));
      showErrorIfSafe('script_loading');
    }
  };
  
  // Add browser console utilities for debugging
  useEffect(() => {
    // Create a debugging utility object that can be accessed from the browser console
    window.debugApplePay = {
      // Get the current state of the component
      getState: () => ({
        orderStatus,
        isPaymentInProgress,
        isApplePayScriptLoaded,
        isLoading,
        timer,
        successFlags: {
          windowFlag: !!window.successfulPaymentReceived,
          sessionStorageFlag: sessionStorage.getItem('successfulPaymentReceived') === 'true',
          navigatingFlag: window.isNavigatingToSuccessPage || sessionStorage.getItem('isNavigatingToSuccessPage') === 'true'
        },
        localStorage: Object.keys(localStorage).reduce((acc, key) => {
          acc[key] = localStorage.getItem(key);
          return acc;
        }, {}),
        sessionStorage: Object.keys(sessionStorage).reduce((acc, key) => {
          acc[key] = sessionStorage.getItem(key);
          return acc;
        }, {})
      }),
      
      // Check the server status
      checkServerStatus: async () => {
        try {
          console.log('%c🔍 Checking server status...', 'background: #e2f3f5; color: #0a3d62; font-size: 14px; font-weight: bold; padding: 4px;');
          const resp = await checkOrderStatus();
          console.log('%c✅ Server status check result:', 'background: #d4edda; color: #155724; font-size: 14px; font-weight: bold; padding: 4px;', resp?.data);
          return resp?.data;
        } catch (err) {
          console.error('%c❌ Server status check failed:', 'background: #f8d7da; color: #721c24; font-size: 14px; font-weight: bold; padding: 4px;', err);
          return null;
        }
      },
      
      // Force show an error modal for testing
      showError: (errorType = 'payment_processing') => {
        console.log('%c🧪 Testing error modal:', 'background: #fff3cd; color: #856404; font-size: 14px; font-weight: bold; padding: 4px;', errorType);
        showErrorIfSafe(errorType);
      },
      
      // Clear success flags for testing
      clearSuccessFlags: () => {
        console.log('%c🧹 Clearing success flags', 'background: #fff3cd; color: #856404; font-size: 14px; font-weight: bold; padding: 4px;');
        window.successfulPaymentReceived = false;
        sessionStorage.removeItem('successfulPaymentReceived');
        window.isNavigatingToSuccessPage = false;
        sessionStorage.removeItem('isNavigatingToSuccessPage');
        return {
          windowFlag: !!window.successfulPaymentReceived,
          sessionStorageFlag: sessionStorage.getItem('successfulPaymentReceived') === 'true',
          navigatingFlag: window.isNavigatingToSuccessPage || sessionStorage.getItem('isNavigatingToSuccessPage') === 'true'
        };
      },
      
      // Get error history
      getErrorHistory: () => {
        return {
          lastError: JSON.parse(sessionStorage.getItem('lastApplePayError') || 'null'),
          lastErrorTrigger: JSON.parse(sessionStorage.getItem('lastApplePayErrorTrigger') || 'null')
        };
      }
    };
    
    console.log('%c🛠️ Apple Pay debugging utilities available', 'background: #d1ecf1; color: #0c5460; font-size: 14px; font-weight: bold; padding: 4px;');
    console.log('Use window.debugApplePay to access debugging utilities:');
    console.log('- window.debugApplePay.getState() - Get current state');
    console.log('- window.debugApplePay.checkServerStatus() - Check server status');
    console.log('- window.debugApplePay.showError(errorType) - Test error modal');
    console.log('- window.debugApplePay.clearSuccessFlags() - Clear success flags');
    console.log('- window.debugApplePay.getErrorHistory() - Get error history');
    
    // Load script on component mount
    loadTranzilaScript();
    
    return () => {
      // Clean up script on unmount
      if (scriptRef.current) {
        try {
          document.body.removeChild(scriptRef.current);
          scriptRef.current = null;
        } catch (err) {
          console.error("ApplePayMobile: Error removing script on unmount:", err);
        }
      }
      
      // Clear any pending timeouts
      if (paymentTimeoutRef.current) {
        clearTimeout(paymentTimeoutRef.current);
      }
      
      // Ensure page loader is hidden on unmount
      setPageLoader(state => ({...state, showLoader: false}));
      
      // Remove debugging utilities
      delete window.debugApplePay;
    };
  }, []);

  // Initialize socket connection and check order status
  useEffect(() => {
    initializeSocket();
    
    // Set up polling for order status as a backup for socket connection issues
    statusPollingRef.current = setInterval(async () => {
      try {
        if (localStorage.getItem("orderId") && orderStatus !== OrderStatus.PAID) {
          debugLog("Polling for order status as backup");
          const resp = await checkOrderStatus();
          
          if (resp && resp.data && resp.data.orderStatus === OrderStatus.PAID) {
            debugLog("Polling detected PAID status, navigating to success page");
            succeed(localStorage.getItem("orderId"));
          }
        }
      } catch (error) {
        console.error("ApplePayMobile: Error in status polling:", error);
      }
    }, 3000); // Check every 3 seconds
    
    const checkInitialOrderStatus = async () => {
      try {
        if (localStorage.getItem("orderId")) {
          const resp = await checkOrderStatus();
          
          if (resp && resp.data) {
            if (resp.data.orderStatus === OrderStatus.PAID) {
              debugLog("Order already paid, redirecting to success page");
              succeed(localStorage.getItem("orderId"));
            } else if (resp.data.orderStatus === OrderStatus.ERROR) {
              debugLog("Order has error status");
              setOrderStatus(OrderStatus.ERROR);
              handleClose();
              showErrorIfSafe('order_status');
              localStorage.removeItem("orderId");
            }
          }
        }
      } catch (error) {
        console.error("ApplePayMobile: Error checking initial order status:", error);
      }
    };
    
    checkInitialOrderStatus();
    
    // Subscribe to status updates
    const unsubscribe = subscribeToOrderStatus((resp) => {
      if (resp && resp.data) {
        debugLog("Received order status update:", resp.data);
        
        if (resp.data.orderStatus === OrderStatus.PAID) {
          succeed(localStorage.getItem("orderId"));
        } else if (resp.data.orderStatus === OrderStatus.ERROR) {
          setOrderStatus(OrderStatus.ERROR);
          handleClose();
          showErrorIfSafe('order_status');
          localStorage.removeItem("orderId");
        }
      }
    });
    
    // Clean up subscription and polling
    return () => {
      unsubscribe();
      if (statusPollingRef.current) {
        clearInterval(statusPollingRef.current);
      }
    };
  }, []);


  // This function is called directly from the button click event (user gesture)
  const handleApplePay = (e) => {
    // Prevent any default behavior
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    debugLog("You clicked the Apple Pay button! Preparing payment...", {
      dataAvailable: !!data,
      dataDetails: {
        amount: data?.amount,
        orderId: data?.orderId,
        handshakeToken: data?.handshakeToken
      }
    });

    // REMOVED: Unconditional 3-second timeout that closed the modal prematurely.
    // setTimeout(() => {
    //   handleClose();
    //   setPageLoader(state => ({...state, showLoader: true}));
    // }, 3000);
    
    // Check if payment is already in progress
    if (isPaymentInProgress) {
      debugLog("Payment is already in progress - please wait for it to complete");
      return;
    }
    
    // Set payment in progress to prevent multiple calls
    setIsPaymentInProgress(true);

    // Show button spinner for 2 seconds
    const buttonElement = applePayButtonRef.current;
    if (buttonElement) {
      buttonElement.disabled = true;
      buttonElement.classList.add('applepay-button--loading');
      setTimeout(() => {
        buttonElement.classList.remove('applepay-button--loading');
        buttonElement.disabled = false;
      }, 2000);
    }

    // Check if script is loaded
    if (!isApplePayScriptLoaded || typeof window.hfChargeApple !== 'function') {
      debugLog("Apple Pay script not loaded or hfChargeApple function not found");
      loadTranzilaScript();
      setIsPaymentInProgress(false);
      showErrorIfSafe('script_loading');
      return;
    }
    
    // Check if we have all required data
    if (!data || !data.amount || !data.orderId || !data.handshakeToken) {
      debugLog("Missing required data:", {
        dataExists: !!data,
        amount: data?.amount,
        orderId: data?.orderId,
        handshakeToken: data?.handshakeToken
      });
      setIsPaymentInProgress(false);
      setPageLoader(state => ({...state, showLoader: false}));

      // Just close the modal and return to payment selection
      // No need to show our error UI for this case
      console.log(`%c🍎 Missing required data - returning to payment selection`, 
        'background: #e2f3f5; color: #0a3d62; font-size: 14px; font-weight: bold; padding: 4px;');
      
      return;
    }
    
    try {
      debugLog("Attempting to launch Apple Pay sheet...");
      // Removed setIsApplePaySheetOpen(true)
      
      // Set up a timeout to detect if the callback is never called
      if (paymentTimeoutRef.current) {
        clearTimeout(paymentTimeoutRef.current);
      }
      
      paymentTimeoutRef.current = setTimeout(() => {
        debugLog("Payment callback not triggered after 30 seconds");
        setIsPaymentInProgress(false);

        // Before showing error, check if payment might have succeeded on server
        const checkServerStatus = async () => {
          try {
            if (localStorage.getItem("orderId")) {
              debugLog("Checking server status before showing timeout error");
              const resp = await checkOrderStatus();
              
              if (resp && resp.data && resp.data.orderStatus === OrderStatus.PAID) {
                debugLog("Server reports payment is successful despite timeout, navigating to success");
                succeed(localStorage.getItem("orderId"));
                return true;
              }
            }
            return false;
          } catch (error) {
            console.error("Error checking server status:", error);
            return false;
          }
        };
        
        checkServerStatus().then(isSuccess => {
          if (!isSuccess) {
            // Only show error if server doesn't report success
            showErrorIfSafe('timeout');
          }
        });
      }, 30000);
      
      // IMPORTANT: This is called directly from a user gesture handler
      // Call the Apple Pay function from the loaded script with all required parameters
      window.hfChargeApple({
        // REQUIRED PARAMS
        labelApple: "תמונות ממוסגרות", // product name (required)
        terminal_name: "blends", // terminal name (required)
        currency_code: "ILS", // currency code (required)
        amount: data.amount, // transaction amount (required)
        tran_mode: "A", // transaction mode (required)
        thtk: data.handshakeToken, // handshake token (required)
        
        // OPTIONAL PARAMS
        response_language: 'english', // api error language
        supported_network: ['visa', 'masterCard', 'amex', 'discover'], // default values
        pdesc: "תמונות ממוסגרות", // product description
        notify_url: `${window.location.origin}/api/apple-pay-webhook?orderId=${data.orderId}`, // notification URL with orderId
        debug: true, // Enable debug mode
      }, function (err, response) {
        // Clear the timeout since the callback was triggered
        if (paymentTimeoutRef.current) {
          clearTimeout(paymentTimeoutRef.current);
          paymentTimeoutRef.current = null;
        }

        debugLog('Apple Pay payment process completed! Processing result...');

        setIsPaymentInProgress(false);
        setPageLoader(state => ({...state, showLoader: false}));
        
        if (err) {
          debugLog("Detailed error in hfChargeApple:", {
            errorMessage: err.message || JSON.stringify(err),
            errorCode: err.code,
            errorType: err.type,
            errorStack: err.stack,
            fullError: JSON.stringify(err)
          });

          // For Apple Pay payment errors, let Apple's UI handle it
          // Just log the error and return to payment selection after a brief delay
          console.log(`%c🍎 Apple Pay error handled by native UI`,
            'background: #e2f3f5; color: #0a3d62; font-size: 14px; font-weight: bold; padding: 4px;');

          // Check if the payment might have succeeded on the server despite the client error
          checkOrderStatus().then(resp => {
            if (resp && resp.data && resp.data.orderStatus === OrderStatus.PAID) {
              debugLog("Despite Apple Pay error, order status is PAID, navigating to success");
              succeed(localStorage.getItem("orderId"));
            } else {
              // Reset state to allow retry - Apple Pay's native UI handles error display
              setIsApplePayScriptLoaded(false);
              loadTranzilaScript();
            }
          }).catch(() => {
            // Reset state to allow retry - Apple Pay's native UI handles error display
            setIsApplePayScriptLoaded(false);
            loadTranzilaScript();
          });

          return;
        }
        
        debugLog('Full response:', {
          responseStatus: response?.status,
          responseDetails: JSON.stringify(response)
        });
        
        // Check for successful transaction - Tranzila returns success in transaction_response
        const isSuccess = response?.transaction_response?.success === true || response?.status === "ok";
        const last4Digits = response?.transaction_response?.credit_card_last_4_digits;

        if (isSuccess) {
            debugLog("Payment successful! 🎉 Updating order status...", {
              orderId: data.orderId,
              amount: data.amount,
              last4Digits: last4Digits,
              responseDetails: JSON.stringify(response)
            });

            // Set success flag
            window.successfulPaymentReceived = true;
            sessionStorage.setItem('successfulPaymentReceived', 'true');

            // Store the order ID in localStorage to ensure it's available for status checks
            if (data.orderId && !localStorage.getItem("orderId")) {
              localStorage.setItem("orderId", data.orderId);
              debugLog("Stored orderId in localStorage:", data.orderId);
            }

            // REVISED NAVIGATION FLOW - Rely on server response
            // Generate a session ID if it doesn't exist (for tracking)
            const sessionId = sessionStorage.getItem('paymentSessionId') ||
                              `session-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;

            if (!sessionStorage.getItem('paymentSessionId')) {
              sessionStorage.setItem('paymentSessionId', sessionId);
            }

            // ADAPTED FLOW (from Credit Card logic):
            // 1. Confirm payment with Tranzila (already done if response.status === "ok")
            // 2. Call server to update status to PAID
            // 3. AFTER server confirms status update, navigate client-side

            debugLog("Payment confirmed by Tranzila. Navigating to success page and updating server status...");

            // Close at once and navigate (no checkmark overlay, no delay) - unless the
            // socket / poll already did. The finalize call below runs either way.
            const oid = data.orderId;
            if (!doneRef.current) {
              doneRef.current = true;
              prepareForSuccessNavigation(setModals, setOverlay);

              console.log('%c🚀 NAVIGATING TO SUCCESS PAGE (payment confirmed by Tranzila)',
                'background: #d4edda; color: #155724; font-size: 14px; font-weight: bold; padding: 4px;', {
                orderId: oid,
                timestamp: new Date().toISOString()
              });

              debugLog(`Navigating to /payment-success/${oid}`);
              goPaySuccess(oid);

              // Clear localStorage after navigation
              setTimeout(() => {
                localStorage.clear();
              }, 100);
            }
            
            // Call the server API to finalize the order (S3 uploads, etc)
            // This is non-blocking - we don't wait for it to complete
            axios.post(`${BASE_URL}/user/changeorderstatus/${data.orderId}`, {
              status: OrderStatus.PAID,
              amountCharged: data.amount,
              sessionId: sessionId,
              last4Digits: last4Digits,
              paymentMethod: 'apple_pay'
            }).then(statusResponse => {
              if (statusResponse.data && statusResponse.data.status === 200) {
                debugLog("Server successfully finalized order (images uploaded to S3).");
              } else {
                debugLog("Server returned non-200 status, but webhook will handle finalization.");
              }
            }).catch(error => {
              // If the API call fails, the webhook will handle the order finalization
              console.log("API call to finalize order failed, webhook will handle it:", error.message);
              debugLog("Client-side finalization failed, relying on webhook for S3 uploads");
            });
        } else {
          debugLog("Payment response not ok:", {
            responseStatus: response?.status,
            responseDetails: JSON.stringify(response)
          });

          // Check server status before closing
          checkOrderStatus().then(resp => {
            if (resp && resp.data && resp.data.orderStatus === OrderStatus.PAID) {
              debugLog("Despite client error, order status is PAID, navigating to success");
              succeed(data.orderId);
            } else {
              // Reset state to allow retry - Apple Pay's native UI handles error display
              console.log(`%c🍎 Payment response not ok - reloading script for retry`,
                'background: #e2f3f5; color: #0a3d62; font-size: 14px; font-weight: bold; padding: 4px;');
              setIsApplePayScriptLoaded(false);
              loadTranzilaScript();
            }
          }).catch(() => {
            // Reset state to allow retry - Apple Pay's native UI handles error display
            setIsApplePayScriptLoaded(false);
            loadTranzilaScript();
          });
        }
      });
    } catch (err) {
      // Clear the timeout if there was an exception
      if (paymentTimeoutRef.current) {
        clearTimeout(paymentTimeoutRef.current);
        paymentTimeoutRef.current = null;
      }

      setIsPaymentInProgress(false);
      setPageLoader(state => ({...state, showLoader: false}));

      debugLog("Exception in handleApplePay:", {
        errorMessage: err.message,
        errorStack: err.stack,
        fullError: JSON.stringify(err)
      });

      // Reset state to allow retry - Apple Pay's native UI handles error display
      console.log(`%c🍎 Exception in handleApplePay - reloading script for retry`,
        'background: #e2f3f5; color: #0a3d62; font-size: 14px; font-weight: bold; padding: 4px;');

      setIsApplePayScriptLoaded(false);
      loadTranzilaScript();
    }
  };

  const handleClose = (isSuccessful = false) => {
    debugLog(`Closing payment modal... (isSuccessful: ${isSuccessful})`);
    
    // Clear any pending timeouts
    if (paymentTimeoutRef.current) {
      clearTimeout(paymentTimeoutRef.current);
      paymentTimeoutRef.current = null;
    }
    
    // Reset payment in progress
    setIsPaymentInProgress(false);
    
    // Call the onClose callback if provided to reset the loading state in the parent component
    if (data && typeof data.onClose === 'function') {
      debugLog("Calling onClose callback to reset parent loading state");
      data.onClose();
    }
    
    // Explicitly ensure all error modals are closed
    setModals(state => ({
      ...state,
      errorCart: {visible: false},
      applePayError: {visible: false, data: {}},
    }));

    if (isSuccessful) {
      // Paid: reset ALL modals at once (no fade, no delay) so none can show
      // over the success page.
      debugLog("Payment successful, resetting ALL modal states");
      resetAllModals(setModals, setOverlay);
      return;
    }

    setIsClosing(true);
    setTimeout(() => {
      // Not successful: only close this modal without reopening selectCardMobile
      setModals(state => ({
        ...state,
        applePayMobile: {visible: false, data: {}},
      }));
      setOverlay(false);
    }, 1000);
  };

  const handleRefreshClick = async () => {
    if (isSecondsPassed && !isPaymentInProgress) {
      setTimer(600);
      
      // Use the existing handshake token for refresh
      debugLog("Refreshing payment attempt with your existing token - please try again");
      
      // Reload the script to ensure it's fresh
      loadTranzilaScript();
      
      // Set a small delay to ensure the script is loaded
      setTimeout(() => {
        handleApplePay();
      }, 500);
      
      setIsRefreshClicked(true);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    const formattedMinutes = String(minutes).padStart(2, "0");
    const formattedSeconds = String(remainingSeconds).padStart(2, "0");
    return `${formattedMinutes}:${formattedSeconds}`;
  };

  // Timer countdown effect
  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimer((prevTimer) => {
        if (prevTimer > 0) {
          return prevTimer - 1;
        } else {
          clearInterval(intervalId);
          handleClose();
          return 0;
        }
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  // Enable refresh button after 20 seconds
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsSecondsPassed(true);
    }, 20000);

    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const loadingTimer = setTimeout(() => {
      if (isApplePayScriptLoaded) {
        debugLog("Script loaded, setting isLoading to false");
        setIsLoading(false);
        setPageLoader(state => ({...state, showLoader: false}));
      } else {
        debugLog("Script not loaded after timeout, forcing isLoading to false");
        setIsLoading(false);
        setPageLoader(state => ({...state, showLoader: false}));
      }
    }, 2000);

    return () => clearTimeout(loadingTimer);
  }, [isApplePayScriptLoaded]);

  if (isLoading) {
    return <div className="applepay-spinner"></div>;
  }

  return (
    <div className={`applepay-body ${!isLoading ? "fadeInModal" : ""} ${isClosing ? "modal-closing" : ""}`}>
      <span className="applepay-timer">{formatTime(timer)}</span>
      <div className="applepay-content">
        <div className="applepay-info">
          <div className="applepay-amount">₪{data?.amount?.toLocaleString('he-IL', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
          })}
            <br />
            <p>בקשה לתשלום מבלנדס:
            < br />
            תמונות ממוסגרות
            </p>
          </div>
        </div>
      </div>
      <p className="applepay-paragraph">
        בחרתם לשלם באמצעות Apple Pay
        <br />
        ההזמנה תכנס ברגע שתשלימו את התשלום
      </p>
      <div className="applepay-buttons">
         <button
           ref={applePayButtonRef} // Assign the ref
           className='applepay-button1' // Base class
           onClick={handleApplePay}
           disabled={isPaymentInProgress} // Still use state for overall disabling logic outside the sheet interaction
         >
           {/* Wrapper for original content */}
           <span className="applepay-button-content">
             Pay <img src="/assets/file/images/apple-icon.svg" alt="Apple Pay"/>
           </span>
           {/* Container for the spinner (initially hidden) */}
           <div className="applepay-button-spinner-container">
             <div className="applepay-button-spinner"></div> {/* Actual spinner element */}
           </div>
         </button>
      </div>
    </div>
  );
};

export default ApplePayMobileModal;
